import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, X, Smartphone } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export function PWAInstallPrompt() {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // Show iOS guide after delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop - listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-bottom" data-testid="pwa-install-banner">
        <div className="max-w-md mx-auto bg-card border border-border shadow-2xl rounded-2xl p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-6 w-6 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-primary">{t('pwa_install_title')}</div>
            <div className="text-xs text-muted-foreground">{t('pwa_install_desc')}</div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" onClick={handleInstall} className="rounded-full gap-1.5" data-testid="pwa-install-btn">
              <Download className="h-3.5 w-3.5" />{t('pwa_install_btn')}
            </Button>
            <button onClick={handleDismiss} className="text-muted-foreground hover:text-primary p-1" data-testid="pwa-dismiss-btn">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center" onClick={handleDismiss}>
          <div className="bg-card rounded-t-2xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} data-testid="ios-install-guide">
            <div className="text-center">
              <div className="font-heading font-bold text-lg text-primary">{t('pwa_ios_title')}</div>
              <div className="text-sm text-muted-foreground mt-1">{t('pwa_ios_desc')}</div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <div className="text-sm">{t('pwa_ios_step1')}</div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <div className="text-sm">{t('pwa_ios_step2')}</div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl">
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">3</div>
                <div className="text-sm">{t('pwa_ios_step3')}</div>
              </div>
            </div>
            <Button className="w-full rounded-full" onClick={handleDismiss}>{t('pwa_ios_close')}</Button>
          </div>
        </div>
      )}
    </>
  );
}
